import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from "react";

/**
 * A page's pull-to-refresh handler. May be async; the gesture host awaits the
 * returned promise to keep the spinner visible until the data settles.
 */
export type PullToRefreshHandler = () => Promise<unknown> | void;

type PullToRefreshContextValue = {
  /** Mutable slot holding the currently-active page's refresh handler. */
  handlerRef: MutableRefObject<PullToRefreshHandler | null>;
  /**
   * App-wide fallback handler used on any page that didn't register its own.
   * Lets pull-to-refresh work everywhere by default (see AppShell), while a
   * page can still register a more specific handler that takes precedence.
   */
  defaultHandlerRef: MutableRefObject<PullToRefreshHandler | null>;
  /** Register (replace) the active handler. */
  setHandler: (handler: PullToRefreshHandler) => void;
  /**
   * Clear the handler, but only if `handler` is still the active one. This
   * guards the mount/unmount race on route change, where React mounts the new
   * page (which registers) *before* unmounting the old page (which clears).
   */
  clearHandler: (handler: PullToRefreshHandler) => void;
  /** Set (or clear, with `null`) the app-wide fallback handler. */
  setDefaultHandler: (handler: PullToRefreshHandler | null) => void;
};

const PullToRefreshContext = createContext<PullToRefreshContextValue | null>(
  null,
);

/**
 * Holds the single, app-wide pull-to-refresh handler registry. One gesture
 * implementation (see PullToRefreshHost) reads from here; each page registers
 * *what* to refresh via `usePullToRefresh`. Pure refs + stable callbacks, so
 * mounting this provider never triggers re-renders of the tree it wraps.
 */
export function PullToRefreshProvider({ children }: { children: ReactNode }) {
  const handlerRef = useRef<PullToRefreshHandler | null>(null);
  const defaultHandlerRef = useRef<PullToRefreshHandler | null>(null);

  const setHandler = useCallback((handler: PullToRefreshHandler) => {
    handlerRef.current = handler;
  }, []);

  const clearHandler = useCallback((handler: PullToRefreshHandler) => {
    if (handlerRef.current === handler) {
      handlerRef.current = null;
    }
  }, []);

  const setDefaultHandler = useCallback(
    (handler: PullToRefreshHandler | null) => {
      defaultHandlerRef.current = handler;
    },
    [],
  );

  const value = useMemo<PullToRefreshContextValue>(
    () => ({
      handlerRef,
      defaultHandlerRef,
      setHandler,
      clearHandler,
      setDefaultHandler,
    }),
    [setHandler, clearHandler, setDefaultHandler],
  );

  return (
    <PullToRefreshContext.Provider value={value}>
      {children}
    </PullToRefreshContext.Provider>
  );
}

function usePullToRefreshContext(): PullToRefreshContextValue | null {
  return useContext(PullToRefreshContext);
}

/** Internal — used by PullToRefreshHost to read the active handler. */
export function usePullToRefreshRegistry(): PullToRefreshContextValue | null {
  return usePullToRefreshContext();
}

/**
 * Register this page's pull-to-refresh handler. The most-recently-mounted page
 * wins; unmounting clears it (race-safe). Pass `enabled: false` to opt a page
 * out dynamically without unmounting.
 *
 * The latest `onRefresh` closure is always used, so callers don't need to
 * memoize it — pass an inline `async () => { await refetch() }`.
 */
export function usePullToRefresh(
  onRefresh: PullToRefreshHandler,
  { enabled = true }: { enabled?: boolean } = {},
): void {
  const ctx = usePullToRefreshContext();
  const latest = useRef(onRefresh);

  useEffect(() => {
    latest.current = onRefresh;
  });

  const setHandler = ctx?.setHandler;
  const clearHandler = ctx?.clearHandler;

  useEffect(() => {
    if (!enabled || !setHandler || !clearHandler) return;
    // Stable indirection so the registered identity never changes across
    // renders — only mount/unmount (and `enabled` flips) touch the registry.
    const handler: PullToRefreshHandler = () => latest.current();
    setHandler(handler);
    return () => clearHandler(handler);
  }, [enabled, setHandler, clearHandler]);
}

/**
 * Register the app-wide fallback pull-to-refresh handler — used on every page
 * that doesn't register its own via `usePullToRefresh`. Mount this once, high in
 * the tree (AppShell), so pull-to-refresh works everywhere instead of only on
 * the handful of pages that opted in. A page's own handler still wins.
 *
 * The latest `onRefresh` closure is always used, so callers don't need to
 * memoize it.
 */
export function useDefaultPullToRefresh(onRefresh: PullToRefreshHandler): void {
  const ctx = usePullToRefreshContext();
  const latest = useRef(onRefresh);

  useEffect(() => {
    latest.current = onRefresh;
  });

  const setDefaultHandler = ctx?.setDefaultHandler;

  useEffect(() => {
    if (!setDefaultHandler) return;
    const handler: PullToRefreshHandler = () => latest.current();
    setDefaultHandler(handler);
    return () => setDefaultHandler(null);
  }, [setDefaultHandler]);
}
